// =======================================================
// „Feedback" in der Kopfzeile
//
// Auf jeder Seite erreichbar: Wer merkt, dass etwas fehlt oder hakt, schreibt
// es in ein, zwei Sätzen auf und schickt es als Ticket ins Ticketsystem.
// Ein Foto der Seite ist schon dabei, weitere Bilder gehen per Büroklammer,
// Ziehen oder Strg+V. Link, Seite, Schritt, Browser und die letzten Fehler
// hängen wir selbst an.
//
// Der Entwurf lässt sich minimieren (X, Escape, Klick daneben): Er liegt im
// meldungStore und überlebt jeden Seitenwechsel. Die schwebende Leiste nimmt
// auf jeder Seite ein weiteres Foto auf.
// =======================================================
import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  MessageSquarePlus, Paperclip, X, Loader2, CheckCircle2, ImageOff, FileText, Send, ExternalLink,
  Camera, Minus, Trash2, PenLine,
} from 'lucide-react';
import { Modal } from '../Modal/Modal';
import { useAuthStore } from '../../stores/authStore';
import { supabase } from '../../lib/supabase';
import { anzahlAnhaenge, hatInhalt, useMeldungStore } from '../../stores/meldungStore';
import {
  ERLAUBTE_TYPEN, MAX_ANHAENGE, dateienPruefen, meldungAbschicken, technischeAngaben,
} from '../../lib/meldung';
import './Melden.css';

interface Props {
  /** So, wie die Rolle oben rechts steht (Administrator, Redaktion, Geschäftsführung, Studioleitung …). */
  rolle: string;
}

const uhr = new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit' });

/**
 * Vorschau-Adresse für eine Datei. Wird im Effekt angelegt und wieder
 * freigegeben — so übersteht sie auch das doppelte Einhängen im Entwicklungsmodus.
 */
function useObjectUrl(datei: File | null): string | null {
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!datei) { setUrl(null); return; }
    const u = URL.createObjectURL(datei);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [datei]);
  return url;
}

/** Fragt nach, wenn schon etwas im Entwurf steht; sonst geht es ohne Rückfrage. */
function verwerfenBestaetigt(): boolean {
  const s = useMeldungStore.getState();
  if (!hatInhalt(s)) return true;
  return window.confirm('Den Feedback-Entwurf verwerfen? Text und Anhänge gehen dabei verloren.');
}

export function MeldenKnopf({ rolle }: Props) {
  const offen = useMeldungStore((s) => s.offen);
  const laufend = useMeldungStore((s) => s.kontext !== null);
  const hatText = useMeldungStore((s) => s.text.trim().length > 0);
  const oeffnen = useMeldungStore((s) => s.oeffnen);
  const hatEntwurf = laufend || hatText;
  const minimiert = laufend && !offen;

  // Solange die Leiste unten steht, bekommt die Seite Luft darunter, damit
  // sich ihr Ende freischieben lässt (siehe Melden.css).
  useEffect(() => {
    document.body.classList.toggle('melden-leiste-aktiv', minimiert);
    return () => document.body.classList.remove('melden-leiste-aktiv');
  }, [minimiert]);

  if (!supabase) return null;

  return (
    <>
      <button
        className="header-btn melden-knopf"
        data-tour="kopf-melden"
        onClick={oeffnen}
        title={hatEntwurf
          ? 'Dein Feedback-Entwurf wartet — hier weiterschreiben'
          : 'Fehlt hier etwas oder hakt es? Direkt als Ticket melden'}
        aria-label={hatEntwurf
          ? 'Feedback geben: Entwurf vorhanden, weiterschreiben'
          : 'Feedback geben: fehlt etwas oder hakt es?'}
      >
        <MessageSquarePlus size={18} />
        <span className="header-btn-text">Feedback</span>
        {hatEntwurf && <span className="melden-punkt" aria-hidden="true" />}
      </button>
      {offen && <MeldenDialog rolle={rolle} />}
      {minimiert && <MeldenLeiste />}
    </>
  );
}

/** Schwebende Leiste, solange der Entwurf minimiert ist. Nie im Foto der Seite. */
function MeldenLeiste() {
  const oeffnen = useMeldungStore((s) => s.oeffnen);
  const verwerfen = useMeldungStore((s) => s.verwerfen);
  const fotoAufnehmen = useMeldungStore((s) => s.fotoAufnehmen);
  const fotoLaeuft = useMeldungStore((s) => s.fotoLaeuft);
  const hinweis = useMeldungStore((s) => s.hinweis);
  const bilder = useMeldungStore(anzahlAnhaenge);

  return createPortal(
    <div className="melden-leiste" role="region" aria-label="Feedback-Entwurf" data-melden-ignorieren>
      <div className="melden-leiste-kopf">
        <MessageSquarePlus size={16} aria-hidden="true" />
        <strong>Feedback-Entwurf</strong>
        <span className="melden-leiste-zahl">{bilder === 1 ? '1 Bild' : `${bilder} Bilder`}</span>
      </div>
      <div className="melden-leiste-aktionen">
        <button type="button" className="btn btn-primary" onClick={oeffnen}>
          <PenLine size={15} aria-hidden="true" /> Weiter schreiben
        </button>
        <button type="button" className="btn btn-secondary" onClick={() => void fotoAufnehmen()} disabled={fotoLaeuft}>
          {fotoLaeuft ? <Loader2 size={15} className="melden-dreht" aria-hidden="true" /> : <Camera size={15} aria-hidden="true" />}
          {fotoLaeuft ? 'Foto wird aufgenommen …' : 'Screenshot dieser Seite'}
        </button>
        <button type="button" className="btn btn-ghost" onClick={() => { if (verwerfenBestaetigt()) verwerfen(); }}>
          <Trash2 size={15} aria-hidden="true" /> Verwerfen
        </button>
      </div>
      <p className="melden-leiste-hinweis" role="status" aria-live="polite">{hinweis}</p>
    </div>,
    document.body,
  );
}

function MeldenDialog({ rolle }: Props) {
  const user = useAuthStore((s) => s.user);
  const {
    kontext, text, screenshot, screenshotStand, screenshotMit, dateien, weitere, ergebnis, hinweis,
  } = useMeldungStore();
  const { setText, setScreenshotMit, dateienSetzen, weitereEntfernen, minimieren, verwerfen, gesendet, schliessen } =
    useMeldungStore.getState();

  const [dateiFehler, setDateiFehler] = useState('');
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState('');
  const [ziehen, setZiehen] = useState(false);
  const dateiInput = useRef<HTMLInputElement>(null);

  const screenshotUrl = useObjectUrl(screenshot);

  const melder = {
    name: user?.fullName ?? '',
    email: user?.email ?? '',
    rolle,
  };
  const mitScreenshot = screenshotMit && !!screenshot;
  // Wie viele Dateien noch dazukönnen: Foto der Seite und weitere Fotos zählen mit.
  const platz = MAX_ANHAENGE - (mitScreenshot ? 1 : 0) - weitere.length;

  const hinzufuegen = useCallback((neu: File[]) => {
    if (neu.length === 0) return;
    const { dateien: naechste, fehler: f } = dateienPruefen(dateien, neu, platz);
    setDateiFehler(f);
    dateienSetzen(naechste);
  }, [dateien, platz, dateienSetzen]);

  const beimEinfuegen = (e: React.ClipboardEvent) => {
    const bilder = Array.from(e.clipboardData.files).filter((f) => f.type.startsWith('image/'));
    if (bilder.length === 0) return;
    e.preventDefault();
    // Eingefügte Bilder heißen im Browser alle „image.png" — durchnummerieren.
    hinzufuegen(bilder.map((f, i) => new File([f], `eingefuegt-${Date.now()}-${i + 1}.png`, { type: f.type })));
  };

  const absenden = async () => {
    if (!kontext) return;
    if (text.trim().length < 10) {
      setFehler('Bitte beschreib kurz, was fehlt oder hakt — ein, zwei Sätze reichen.');
      return;
    }
    setFehler('');
    setSendet(true);
    try {
      const anhaenge = [
        ...(mitScreenshot && screenshot ? [screenshot] : []),
        ...weitere.map((w) => w.datei),
        ...dateien,
      ];
      const erg = await meldungAbschicken({
        beschreibung: text,
        kontext,
        melder,
        anhaenge,
        mitScreenshot,
        weitere: weitere.map((w) => w.kontext),
      });
      gesendet(erg);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : 'Das hat nicht geklappt. Bitte versuch es noch einmal.');
    } finally {
      setSendet(false);
    }
  };

  if (ergebnis) {
    return (
      <div className="melden-huelle" data-melden-ignorieren>
        <Modal isOpen onClose={schliessen} title="Danke für deine Meldung" size="sm">
          <div className="melden-danke">
            <CheckCircle2 size={44} className="melden-danke-icon" aria-hidden="true" />
            <p>Dein Ticket ist angelegt:</p>
            <p className="melden-nummer">{ergebnis.nummer}</p>
            <p className="form-hint">
              Du bekommst eine Bestätigung per Mail. Den Stand und Rückfragen siehst du unter
              „Meine Tickets" im Ticketsystem.
            </p>
            {ergebnis.anhaengeFehlgeschlagen > 0 && (
              <p className="melden-warnung">
                {ergebnis.anhaengeFehlgeschlagen === 1 ? 'Ein Anhang' : `${ergebnis.anhaengeFehlgeschlagen} Anhänge`}{' '}
                ließ sich nicht hochladen. Du kannst ihn unter „Meine Tickets" nachreichen.
              </p>
            )}
          </div>
          <div className="form-actions">
            {ergebnis.meineTicketsUrl && (
              <a className="btn btn-secondary" href={ergebnis.meineTicketsUrl} target="_blank" rel="noopener noreferrer">
                <ExternalLink size={15} /> Meine Tickets
              </a>
            )}
            <button className="btn btn-primary" onClick={schliessen} autoFocus>Schließen</button>
          </div>
        </Modal>
      </div>
    );
  }

  if (!kontext) return null;

  return (
    <div className="melden-huelle" data-melden-ignorieren>
      {/* X, Escape und Klick daneben minimieren — der Entwurf bleibt. */}
      <Modal isOpen onClose={() => { if (!sendet) minimieren(); }} title="Fehlt hier etwas?" size="md">
        <div
          className={`melden${ziehen ? ' melden-ziehen' : ''}`}
          onDragOver={(e) => { e.preventDefault(); setZiehen(true); }}
          onDragLeave={() => setZiehen(false)}
          onDrop={(e) => { e.preventDefault(); setZiehen(false); hinzufuegen(Array.from(e.dataTransfer.files)); }}
        >
          <div className="melden-ort">
            <span className="melden-ort-label">Seite</span> {kontext.seite}
            {weitere.length > 0 && (
              <>
                <span className="melden-ort-label melden-ort-weitere">Weitere Seiten mit Foto</span>
                <ol className="melden-weitere">
                  {weitere.map((w, i) => (
                    <li key={`${w.datei.name}-${i}`}>{w.kontext.seite} · {uhr.format(w.kontext.zeitpunkt)} Uhr</li>
                  ))}
                </ol>
              </>
            )}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="melden-text">Was fehlt oder hakt?</label>
            <textarea
              id="melden-text"
              className="form-textarea"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onPaste={beimEinfuegen}
              placeholder={'z. B. Hier wäre eine Auswahl „Für FBBC-Mitglieder kostenlos" gut — neben der e.V.-Option.'}
              rows={5}
              maxLength={5000}
              autoFocus
              disabled={sendet}
            />
            <p className="form-hint">
              Ein, zwei Sätze reichen. Link, Seite und technische Angaben hängen wir selbst an.
            </p>
          </div>

          <div className="form-group">
            <span className="form-label">Bilder</span>
            <div className="melden-anhaenge">
              {screenshotStand === 'laeuft' && (
                <div className="melden-kachel melden-kachel-laedt">
                  <Loader2 size={18} className="melden-dreht" aria-hidden="true" />
                  <span>Foto der Seite …</span>
                </div>
              )}
              {screenshotStand === 'fertig' && screenshotUrl && (
                <div className={`melden-kachel melden-kachel-bild${screenshotMit ? '' : ' aus'}`}>
                  <a href={screenshotUrl} target="_blank" rel="noopener noreferrer" title="Groß ansehen">
                    <img src={screenshotUrl} alt="Foto der aktuellen Seite" />
                  </a>
                  <span className="melden-kachel-name">{screenshotMit ? 'Foto dieser Seite' : 'Foto weggelassen'}</span>
                  <button
                    type="button"
                    className="melden-kachel-weg"
                    onClick={() => setScreenshotMit(!screenshotMit)}
                    title={screenshotMit ? 'Foto nicht mitschicken' : 'Foto doch mitschicken'}
                    aria-label={screenshotMit ? 'Foto nicht mitschicken' : 'Foto doch mitschicken'}
                    disabled={sendet}
                  >
                    {screenshotMit ? <X size={14} /> : <ImageOff size={14} />}
                  </button>
                </div>
              )}
              {weitere.map((w, i) => (
                <DateiKachel
                  key={`${w.datei.name}-${i}`}
                  datei={w.datei}
                  onWeg={() => weitereEntfernen(i)}
                  gesperrt={sendet}
                />
              ))}
              {dateien.map((f, i) => (
                <DateiKachel
                  key={`${f.name}-${i}`}
                  datei={f}
                  onWeg={() => dateienSetzen(dateien.filter((_, j) => j !== i))}
                  gesperrt={sendet}
                />
              ))}
              {dateien.length < platz && (
                <button
                  type="button"
                  className="melden-kachel melden-kachel-neu"
                  onClick={() => dateiInput.current?.click()}
                  disabled={sendet}
                >
                  <Paperclip size={18} aria-hidden="true" />
                  <span>Bild anhängen</span>
                </button>
              )}
              {dateien.length < platz && (
                <button
                  type="button"
                  className="melden-kachel melden-kachel-neu"
                  onClick={() => minimieren(true)}
                  disabled={sendet}
                  title="Der Entwurf wird klein; geh zur gewünschten Seite und klick unten auf „Screenshot dieser Seite“"
                  data-melden-andere-seite
                >
                  <Camera size={18} aria-hidden="true" />
                  <span>Andere Seite fotografieren</span>
                </button>
              )}
            </div>
            <input
              ref={dateiInput}
              type="file"
              accept={ERLAUBTE_TYPEN}
              multiple
              hidden
              onChange={(e) => { hinzufuegen(Array.from(e.target.files ?? [])); e.target.value = ''; }}
            />
            <p className="form-hint">
              {screenshotStand === 'fehlt'
                ? 'Das automatische Foto hat nicht geklappt — häng gern selbst einen Screenshot an. '
                : ''}
              Auch per Ziehen oder Strg+V ins Textfeld. Bilder und PDF, bis 10 MB, höchstens {MAX_ANHAENGE} Anhänge.
            </p>
            {dateiFehler && <p className="melden-warnung">{dateiFehler}</p>}
            {hinweis && <p className="melden-warnung" role="status">{hinweis}</p>}
          </div>

          <details className="melden-technik">
            <summary>Was automatisch mitgeht</summary>
            <pre>{technischeAngaben(kontext, melder, mitScreenshot, weitere.map((w) => w.kontext))}</pre>
          </details>

          {fehler && <p className="melden-fehler" role="alert">{fehler}</p>}

          <p className="form-hint melden-minimier-hinweis">
            Mit dem X oder Escape wird der Entwurf nur klein — er bleibt beim Weiterklicken erhalten.
          </p>

          <div className="form-actions">
            <button
              className="btn btn-ghost"
              onClick={() => { if (verwerfenBestaetigt()) verwerfen(); }}
              disabled={sendet}
            >
              Entwurf verwerfen
            </button>
            <button className="btn btn-secondary" onClick={() => minimieren(true)} disabled={sendet}>
              <Minus size={15} aria-hidden="true" /> Minimieren
            </button>
            <button
              className="btn btn-primary"
              onClick={() => void absenden()}
              disabled={sendet || screenshotStand === 'laeuft'}
              title={screenshotStand === 'laeuft' ? 'Einen Moment — das Foto der Seite entsteht noch' : undefined}
            >
              {sendet ? <Loader2 size={15} className="melden-dreht" /> : <Send size={15} />}
              {sendet ? 'Wird gesendet …' : 'Als Ticket senden'}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}

function DateiKachel({ datei, onWeg, gesperrt }: { datei: File; onWeg: () => void; gesperrt: boolean }) {
  const istBild = datei.type.startsWith('image/') && !/hei[cf]/.test(datei.type);
  const url = useObjectUrl(istBild ? datei : null);

  return (
    <div className="melden-kachel melden-kachel-bild">
      {url ? <img src={url} alt="" /> : <FileText size={22} className="melden-kachel-datei" aria-hidden="true" />}
      <span className="melden-kachel-name" title={datei.name}>{datei.name}</span>
      <button
        type="button"
        className="melden-kachel-weg"
        onClick={onWeg}
        title="Entfernen"
        aria-label={`${datei.name} entfernen`}
        disabled={gesperrt}
      >
        <X size={14} />
      </button>
    </div>
  );
}
